import { db } from './firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

// Basic mock data
const MOCK_WORDS = [
  'Sole',
  'Mare',
  'Amore',
  'Pizza',
  'Gatto',
  'Casa',
  'Fuoco',
  'Acqua',
  'Libro',
  'Viaggio',
  'Musica',
  'Computer',
  'Telefono',
  'Cane',
  'Albero',
  'Fiore',
  'Montagna',
  'Città',
  'Sogno',
  'Tempo',
];

const RECENT_KEY = 'chain_recent_words';
const RECENT_MAX = 300; // finestra di esclusione
const FETCH_POOL = 600; // parole prelevate per partita dal DB

/** Fisher-Yates shuffle in-place */
function fisherYates(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function getRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecent(recent) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(-RECENT_MAX)));
  } catch {
    /* ignora errori quota */
  }
}

function updateRecent(newWords) {
  // newWords are objects {id, text}, we save just the text for recent exclusion
  const texts = newWords.map((w) => w.text);
  saveRecent([...getRecent(), ...texts]);
}

export const wordService = {
  // Fetch words for standard game — cursor pagination attraverso tutto il DB
  getWords: async (count = 50) => {
    try {
      // Pick a random starting point in the [0, 1] range
      const randomStart = Math.random();

      // Query words ordered by the 'random' field
      const q = query(
        collection(db, 'words'),
        orderBy('random'),
        startAfter(randomStart),
        limit(FETCH_POOL),
      );

      let snap = await getDocs(q);
      let docs = snap.docs;

      // If we didn't get enough words (near the end of the [0, 1] range),
      // wrap around and fetch from the beginning
      if (docs.length < count) {
        const wrapQ = query(collection(db, 'words'), orderBy('random'), limit(FETCH_POOL));
        const wrapSnap = await getDocs(wrapQ);
        // Merge, avoiding duplicates
        const existingIds = new Set(docs.map((d) => d.id));
        const extra = wrapSnap.docs.filter((d) => !existingIds.has(d.id));
        docs = [...docs, ...extra];
      }

      let allWords = docs.map((d) => ({
        id: d.id,
        text: d.data().text || d.id,
      }));

      // Escludi le frasi (parole con spazio) dal pool normale
      allWords = allWords.filter((w) => !w.text.includes(' '));

      // Escludi le parole usate di recente (se il pool è abbastanza grande)
      const recent = new Set(getRecent());
      const fresh = allWords.filter((w) => !recent.has(w.text));
      const pool = fresh.length >= count ? fresh : allWords;

      // Shuffle vera con Fisher-Yates e seleziona
      fisherYates(pool);
      const selected = pool.slice(0, count);

      // Aggiorna lista recenti
      updateRecent(selected);

      return selected;
    } catch (error) {
      console.error('Error fetching words:', error);
      const mock = MOCK_WORDS.map((w) => ({ id: w.toUpperCase(), text: w }));
      return fisherYates([...mock]).slice(0, count);
    }
  },

  // Fetch phrase-words (parole che contengono almeno uno spazio) per il Raddoppio
  getPhraseWords: async (count = 20) => {
    try {
      const randomStart = Math.random();
      const q = query(
        collection(db, 'words'),
        orderBy('random'),
        startAfter(randomStart),
        limit(FETCH_POOL),
      );
      let snap = await getDocs(q);
      let docs = snap.docs;

      if (docs.length < count) {
        const wrapQ = query(collection(db, 'words'), orderBy('random'), limit(FETCH_POOL));
        const wrapSnap = await getDocs(wrapQ);
        const existingIds = new Set(docs.map((d) => d.id));
        const extra = wrapSnap.docs.filter((d) => !existingIds.has(d.id));
        docs = [...docs, ...extra];
      }

      // Tieni SOLO le frasi (testo con spazio)
      let phrases = docs
        .map((d) => ({ id: d.id, text: d.data().text || d.id }))
        .filter((w) => w.text.includes(' '));

      if (phrases.length === 0) {
        console.warn('[wordService] Nessuna frase trovata nel DB per il Raddoppio.');
        return [];
      }

      fisherYates(phrases);
      return phrases.slice(0, count);
    } catch (error) {
      console.error('[wordService] Errore nel fetch delle frasi:', error);
      return [];
    }
  },

  // Fetch Top N Frequent words for training (mantiene ordinamento per frequenza)
  getTrainingWords: async (count = 50) => {
    try {
      const q = query(collection(db, 'words'), orderBy('frequency', 'desc'), limit(count));
      const snap = await getDocs(q);
      // Filter out empty or non-string values to prevent crashes downstream
      const words = snap.docs
        .map((doc) => ({
          id: doc.id,
          text: doc.data().text,
        }))
        .filter((w) => typeof w.text === 'string' && w.text.length > 0);

      if (words.length === 0) {
        console.warn('No training words found in DB, using fallback.');
        const mock = MOCK_WORDS.map((w) => ({ id: w.toUpperCase(), text: w }));
        return fisherYates([...mock]).slice(0, count);
      }

      return fisherYates([...words]);
    } catch (error) {
      console.error('Error fetching training words:', error);
      const mock = MOCK_WORDS.map((w) => ({ id: w.toUpperCase(), text: w }));
      return fisherYates([...mock]).slice(0, count);
    }
  },

  /**
   * Updates global statistics for a word.
   * @param {string} wordId - Document ID in 'words' collection
   * @param {'correct' | 'wrong' | 'pass'} outcome - The result of the guess
   * @param {number} time - Time taken to guess (in seconds), used for 'correct' average
   */
  updateWordStats: async (wordId, outcome, time = 0) => {
    if (!wordId) return;
    const wordRef = doc(db, 'words', wordId);

    try {
      await runTransaction(db, async (transaction) => {
        const wordDoc = await transaction.get(wordRef);

        // Default data if doc doesn't exist (though it should)
        let current = wordDoc.exists()
          ? wordDoc.data()
          : {
              corrette: 0,
              passate: 0,
              errate: 0,
              tempo: 0,
              status: 'DUBBIO',
            };

        const updates = {
          lastPlayedAt: serverTimestamp(),
        };

        if (outcome === 'correct') {
          const oldCorrette = current.corrette || 0;
          const oldTempo = current.tempo || 0;
          updates.corrette = oldCorrette + 1;
          // Calculate new average time
          updates.tempo = (oldTempo * oldCorrette + time) / (oldCorrette + 1);
          updates.status = 'CONFERMATO';
        } else if (outcome === 'wrong') {
          updates.errate = (current.errate || 0) + 1;
        } else if (outcome === 'pass') {
          updates.passate = (current.passate || 0) + 1;
        }

        transaction.set(wordRef, updates, { merge: true });
      });
    } catch (error) {
      console.error(`[wordService] Failed to update stats for word ${wordId}:`, error);
    }
  },

  /**
   * Marks a word as reported in the database.
   * @param {string} wordId - Document ID in 'words' collection
   */
  reportWord: async (wordId) => {
    if (!wordId) return;
    const wordRef = doc(db, 'words', wordId);
    try {
      await runTransaction(db, async (transaction) => {
        transaction.set(
          wordRef,
          {
            status: 'SEGNALATA',
            reportedAt: serverTimestamp(),
          },
          { merge: true },
        );
      });
    } catch (error) {
      console.error(`[wordService] Failed to report word ${wordId}:`, error);
    }
  },
};
